import * as React from 'react'
import { ReactElement, useEffect, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import TaskList, { TaskListProps } from '../components/List/Task/TaskList'
import FileList from '../components/List/File/FileList'
import { Stack, StackItem, Text } from '@fluentui/react'
import { isOfficeInitialized } from '../helpers/outlookHelper'
import { COMMAND_PREFIX } from './Commands'
import { useHistory } from 'react-router-dom'
import { taskBaseUrl } from './Tasks'
import { MenuItemKey } from '../components/Menu'
import { AppAction } from '../config/appConfig'
import { FileAction, IOverviewItemDto } from '../config/fileConfig'
import useEntity from '../hooks/useEntity'
import RibbonButton from '../components/RibbonButton'
import { TaskOverviewDto, TaskStatus } from '../config/taskConfig'
import { CustomIconSrc } from '../helpers/iconHelper'
import useUser from '../hooks/useUser'
import { ModuleType, SortDirection } from '../context/GlobalContext'
import useModules from '../hooks/module/useModules'
import useRoles, { rolesTasks, rolesTimes } from '../hooks/useRoles'
import { useSettings } from '../hooks/useSettings'
import { useTranslation } from 'react-i18next'

const taskListProps: Omit<TaskListProps, 'reset' | 'data'> = {
    baseUrl: taskBaseUrl,
    global: true,
}

export default function Dashboard(props): ReactElement {
    const settings = useSettings()
    const { id: userId } = useUser()
    const dispatch = useDispatch()
    const modules = useModules()
    const roles = useRoles()
    const history = useHistory()
    const { t: translate } = useTranslation()
    const hasTaskAuthorization = useMemo<boolean>(() => roles.any(rolesTasks), [roles.loaded])
    const hasTimeAuthorization = useMemo<boolean>(() => roles.any(rolesTimes), [roles.loaded])
    const showRecentFiles = useMemo(() => settings?.dashboard?.showRecentFiles !== false, [settings])

    const [assigneeTasks, , resetAssigneeTasks] = useEntity<TaskOverviewDto[]>(
        userId => ({ url: '/task', params: { assignees: [userId], statuses: [TaskStatus.Open] } }),
        hasTaskAuthorization ? userId : undefined,
    )
    const [assignerTasks, , resetAssignerTasks] = useEntity<TaskOverviewDto[]>(
        userId => ({ url: '/task', params: { assigners: [userId], statuses: [TaskStatus.Open] } }),
        hasTaskAuthorization ? userId : undefined,
    )
    const [recentFiles] = useEntity<IOverviewItemDto[]>(
        () => ({ url: '/history/most-recent-files', params: { size: 25 } }),
        false,
    )

    useEffect(() => {
        dispatch({ type: AppAction.SetBreadcrumbs, payload: [MenuItemKey.Dashboard] })
        dispatch({ type: FileAction.ResetCurrent })
    }, [props])

    return (
        <Stack tokens={{ childrenGap: 'm' }}>
            {!isOfficeInitialized() && (
                <Stack horizontal horizontalAlign="center" wrap grow>
                    {modules.has(ModuleType.TimeTracking) && hasTimeAuthorization && (
                        <RibbonButton
                            iconSrc={CustomIconSrc.AddTime}
                            text={translate('action.create.time.title')}
                            onClick={() => history.push(`${COMMAND_PREFIX}/add-time`)}
                        />
                    )}
                    <RibbonButton
                        iconSrc={CustomIconSrc.SearchFile}
                        text={translate('action.search.file.title')}
                        onClick={() => history.push(`${COMMAND_PREFIX}/search-file`)}
                    />
                    {modules.has(ModuleType.Tasks) && hasTaskAuthorization && (
                        <RibbonButton
                            iconSrc={CustomIconSrc.AddTask}
                            text={translate('action.create.task.title')}
                            onClick={() => history.push(`${COMMAND_PREFIX}/add-task`)}
                        />
                    )}
                </Stack>
            )}
            {modules.has(ModuleType.Tasks) && hasTaskAuthorization && (
                <>
                    <StackItem>
                        <Text className="ms-font-xl">{translate('dashboard.task.assignee.title')}</Text>
                        <TaskList {...taskListProps} reset={resetAssigneeTasks} data={assigneeTasks.data} />
                    </StackItem>
                    <StackItem>
                        <Text className="ms-font-xl">{translate('dashboard.task.assigner.title')}</Text>
                        <TaskList
                            {...taskListProps}
                            reset={resetAssignerTasks}
                            data={assignerTasks.data}
                            settings={{ excludeOwnerIsAssignee: true }}
                        />
                    </StackItem>
                </>
            )}
            {showRecentFiles && (
                <StackItem>
                    <Text className="ms-font-xl">{translate('dashboard.file.title')}</Text>
                    <FileList
                        files={recentFiles}
                        sorting={{ direction: SortDirection.Descending, field: 'lastAccess' }}
                    />
                </StackItem>
            )}
        </Stack>
    )
}
